<div class="address-verification-report">
  <!-- Page Control for export of data -->
  <PageControl
    :title="'Address Verification Report'"
    :elements_query="sheet_elements"
  >
    <button
      @click="show_export_modal"
      class="btn btn-bg-green text-xs rounded-small shadow-sm me-2"
    >
      <i class="bi bi-download"></i>
      Export Data
    </button>

    <!-- Export Modal -->
    <Teleport to="body">
      <div
        class="modal fade"
        :id="'export-modal-' + report_reference"
        tabindex="-1"
        aria-labelledby="exportModalLabel"
        aria-hidden="true"
      >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content rounded-4 shadow">
          <div class="modal-header">
            <h6 class="modal-title fw-semibold text-muted">
              Export Address Verification Data
            </h6>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label fw-semibold">Export Sections</label>
              <div class="form-check">
                <input
                  class="form-check-input"
                  type="checkbox"
                  id="consumer_details"
                  v-model="export_options.consumer_details"
                />
                <label class="form-check-label" for="consumer_details">
                  Consumer Details
                </label>
              </div>
              <div class="form-check">
                <input
                  class="form-check-input"
                  type="checkbox"
                  id="address_history"
                  v-model="export_options.address_history"
                />
                <label class="form-check-label" for="address_history">
                  Address History
                </label>
              </div>
              <div class="form-check">
                <input
                  class="form-check-input"
                  type="checkbox"
                  id="address_analysis"
                  v-model="export_options.address_analysis"
                />
                <label class="form-check-label" for="address_analysis">
                  Address Analysis
                </label>
              </div>
              <div class="form-check" v-if="export_format === 'json'">
                <input
                  class="form-check-input"
                  type="checkbox"
                  id="raw_json"
                  v-model="export_options.raw_json"
                />
                <label class="form-check-label" for="raw_json">
                  Raw JSON Data
                </label>
              </div>
            </div>
            <div class="mb-3">
              <label class="form-label fw-semibold">Export Format</label>
              <select class="form-select" v-model="export_format">
                <option value="csv">CSV</option>
                <option value="excel">Excel</option>
                <option value="json">JSON</option>
              </select>
            </div>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-bg-secondary text-xs rounded-small shadow-sm me-2"
              @click="hide_export_modal"
            >
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-bg-green text-xs rounded-small shadow-sm"
              :disabled="!has_export_options_selected"
              @click="export_data"
            >
              <i class="bi bi-download me-1"></i>Export
            </button>
          </div>
        </div>
      </div>
    </div>
    </Teleport>
  </PageControl>

  <PageComponent>
    <!-- Page 1: Report Header & Consumer Information -->
    <SectionSheet>
      <div
        class="container-fluid py-3 header d-flex align-items-center justify-content-between border-bottom mb-3"
      >
        <div class="d-flex">
          <img
            class="logo"
            width="295"
            height="125"
            src="@/assets/images/new_black_logo2.svg"
            alt="Logo"
          />
        </div>
        <div class="d-flex text-end">
          <div>
            <div class="h4 fw-bold mb-1">Address Verification Report</div>
            <div class="text-muted text-xs">
              28 Fricker Rd,<br />
              Illovo, Johannesburg,<br />
              2196 South Africa<br />
              Website:
              <a
                href="https://www.goodpeopledata.com/"
                target="_blank"
                class="text-decoration-none text-muted"
                >www.goodpeopledata.com</a
              >
            </div>
          </div>
        </div>
      </div>

      <!-- Verification Summary -->
      <div class="card shadow-sm border bg-white mb-3">
        <div class="card-header">
          <h6 class="mb-0">
            <i class="bi bi-house-check me-2"></i>Verification Summary
          </h6>
        </div>
        <div class="card-body text-xs">
          <p class="mb-3">
            This report provides a comprehensive analysis of the address
            verification for
            <strong>{{ consumer_full_name }}</strong> (ID: {{ consumer_id_number
            }}). The verification includes current and historical address
            information sourced from credit bureau records.
          </p>
          <div class="row g-2">
            <div class="col-md-4">
              <div class="status-card-simple">
                <div class="status-icon-simple">
                  <i class="bi bi-person-check text-success" v-if="safe_address_data"></i>
                    <i class="bi bi-person-x text-danger" v-else></i>
                </div>
                <div class="status-content-simple">
                  <div class="status-label-simple">Address Data</div>
                  <div class="status-value-simple text-success" v-if="safe_address_data">Retrieved</div>
                    <div class="status-value-simple text-danger" v-else>Not Retrieved</div>
                </div>
              </div>
            </div>
            <div class="col-md-4">
              <div class="status-card-simple">
                <div class="status-icon-simple">
                  <i class="bi bi-houses text-info"></i>
                </div>
                <div class="status-content-simple">
                  <div class="status-label-simple">Total Addresses</div>
                  <div class="status-value-simple text-info">
                    {{ total_addresses }}
                  </div>
                </div>
              </div>
            </div>
            <div class="col-md-4">
              <div class="status-card-simple">
                <div class="status-icon-simple">
                  <i class="bi bi-calendar-range text-warning"></i>
                </div>
                <div class="status-content-simple">
                  <div class="status-label-simple">Date Range</div>
                  <div class="status-value-simple text-warning">
                    {{ address_date_range }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Consumer Details -->
      <div class="card shadow-sm border bg-white mb-3">
        <div class="card-header">
          <h6 class="mb-0">
            <i class="bi bi-person-vcard me-2"></i>Personal Information
          </h6>
        </div>
        <div class="card-body text-xs">
          <div class="info-grid">
            <div class="info-item">
              <span class="label">Consumer ID</span>
              <span class="value">{{ consumer_id }}</span>
            </div>
            <div class="info-item">
              <span class="label">Full Name</span>
              <span class="value">{{ consumer_full_name }}</span>
            </div>
            <div class="info-item">
              <span class="label">ID Number</span>
              <span class="value">{{ consumer_id_number }}</span>
            </div>
            <div class="info-item">
              <span class="label">Birth Date</span>
              <span class="value">{{ formatted_birth_date }}</span>
            </div>
            <div class="info-item">
              <span class="label">Gender</span>
              <span class="value">{{ consumer_gender }}</span>
            </div>
            <div class="info-item">
              <span class="label">Marital Status</span>
              <span class="value">{{ consumer_marital_status }}</span>
            </div>
            <div class="info-item">
              <span class="label">Home Phone</span>
              <span class="value"
                >{{ consumer_home_phone || 'Not Available' }}</span
              >
            </div>
            <div class="info-item">
              <span class="label">Work Phone</span>
              <span class="value"
                >{{ consumer_work_phone || 'Not Available' }}</span
              >
            </div>
            <div class="info-item">
              <span class="label">Mobile Phone</span>
              <span class="value"
                >{{ consumer_mobile_phone || 'Not Available' }}</span
              >
            </div>
            <div class="info-item">
              <span class="label">Email</span>
              <span class="value">{{ consumer_email || 'Not Available' }}</span>
            </div>
            <div class="info-item">
              <span class="label">Employer</span>
              <span class="value"
                >{{ consumer_employer || 'Not Available' }}</span
              >
            </div>
            <div class="info-item">
              <span class="label">Privacy Status</span>
              <span class="value">{{ consumer_privacy_status }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Report Footer -->
      <div class="report-footer">
        <span
          >Generated: {{ generated_date }} | Reference: {{ report_reference
          }}</span
        >
        <span>Page 1 of {{ 1 + paginated_address_history.length + 2 }}</span>
      </div>
    </SectionSheet>

    <SectionSheet>
      <!-- Address Summary Cards -->
      <div class="row g-2 mb-2">
        <div class="col-md-4">
          <div class="summary-card shadow-sm border bg-white residential">
            <div class="summary-icon">
              <i class="bi bi-house-door"></i>
            </div>
            <div class="summary-content">
              <div class="summary-label">Residential Addresses</div>
              <div class="summary-value">{{ residential_address_count }}</div>
              <div class="summary-detail">
                {{ residential_address_percentage }}% of total
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-4">
          <div class="summary-card shadow-sm border bg-white postal">
            <div class="summary-icon">
              <i class="bi bi-mailbox"></i>
            </div>
            <div class="summary-content">
              <div class="summary-label">Postal Addresses</div>
              <div class="summary-value">{{ postal_address_count }}</div>
              <div class="summary-detail">
                {{ postal_address_percentage }}% of total
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-4">
          <div class="summary-card shadow-sm border bg-white unique">
            <div class="summary-icon">
              <i class="bi bi-geo-alt"></i>
            </div>
            <div class="summary-content">
              <div class="summary-label">Unique Locations</div>
              <div class="summary-value">{{ unique_address_count }}</div>
              <div class="summary-detail">Distinct addresses</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Current Address Information -->
      <div class="card shadow-sm border bg-white mb-3">
        <div class="card-header">
          <h6 class="mb-0">
            <i class="bi bi-house-check me-2"></i>Current Address Information
          </h6>
        </div>
        <div class="card-body text-xs">
          <div class="row g-2">
            <div class="col-md-12">
              <div class="info-grid">
                <div class="info-item">
                  <span class="label">Residential Address</span>
                  <span class="value">{{ current_residential_address }}</span>
                </div>
                <div class="info-item">
                  <span class="label">Postal Address</span>
                  <span class="value">{{ current_postal_address }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- Address Analysis -->
      <div class="card shadow-sm border bg-white mb-3">
        <div class="card-header">
          <h6 class="mb-0">
            <i class="bi bi-graph-up me-2"></i>Address Analysis
          </h6>
        </div>
        <div class="card-body text-xs">
          <div class="row g-2">
            <div class="col-md-12 px-2">
              <div class="info-grid">
                <div class="info-item">
                  <span class="label">Total Address Records</span>
                  <span class="value">{{ total_addresses }}</span>
                </div>
                <div class="info-item">
                  <span class="label">Residential Addresses</span>
                  <span class="value">{{ residential_address_count }}</span>
                </div>
                <div class="info-item">
                  <span class="label">Postal Addresses</span>
                  <span class="value">{{ postal_address_count }}</span>
                </div>
                <div class="info-item">
                  <span class="label">Unique Locations</span>
                  <span class="value">{{ unique_address_count }}</span>
                </div>
                <div class="info-item">
                  <span class="label">Most Common Postal Code</span>
                  <span class="value">{{ most_common_postal_code }}</span>
                </div>
                <div class="info-item">
                  <span class="label">Address Updates</span>
                  <span class="value">{{ total_address_updates }}</span>
                </div>
                <div class="info-item">
                  <span class="label">Current Address Age</span>
                  <span class="value">{{ current_address_age }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Report Footer -->
      <div class="report-footer">
        <span
          >Generated: {{ generated_date }} | Reference: {{ report_reference
          }}</span
        >
        <span
          >Page 2 of {{ 1 +
          paginated_address_history.length + 2 }}</span
        >
      </div>
    </SectionSheet>

    <template
      v-for="(page_addresses, page_index) in paginated_address_history"
      :key="'address-history-page-' + (page_index + 1)"
    >
      <SectionSheet>
        <!-- Address History Table -->
        <div class="card shadow-sm border bg-white mb-3">
          <div class="card-header">
            <h6 class="mb-0">
              <i class="bi bi-clock-history me-2"></i>Complete Address History
              (Page {{ page_index + 1 }} of {{ paginated_address_history.length
              }})
            </h6>
          </div>
          <div class="card-body text-xs">
            <div class="table-responsive">
              <table class="table table-sm">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Address</th>
                    <th>Postal Code</th>
                    <th>Type</th>
                    <th>Date Reported</th>
                    <th>Last Updated</th>                    
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(address, idx) in page_addresses"
                    :key="address.id || idx"
                  >
                    <td>{{ idx + 1 + page_index * 20 }}</td>
                    <td class="address-cell">
                      {{ normalize_address(address.FullAddress || address) }}
                    </td>
                    <td class="postal-cell">{{ address.PostalCode }}</td>
                    <td class="type-cell">
                      <span
                        :class="'type-badge ' + get_address_type_class(address.AddressType)"
                        >{{ address.AddressType }}</span
                      >
                    </td>
                    <td class="date-cell">
                      {{ formatDate(address.FirstReportedDate) }}
                    </td>
                    <td class="date-cell">
                      {{ formatDate(address.LastUpdatedDate) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Report Footer -->
        <div class="report-footer">
          <span
            >Generated: {{ generated_date }} | Reference: {{ report_reference
            }}</span
          >
          <span
            >Page {{ 3 + page_index }} of {{ 1 +
            paginated_address_history.length + 2 }}</span
          >
        </div>
      </SectionSheet>
    </template>

    <!-- Disclaimer and Information Section -->
    <SectionSheet>
      <div class="card shadow-sm border bg-white mb-2">
        <div class="card-header bg-light">
          <h6 class="mb-0">
            <i class="bi bi-exclamation-triangle-fill me-2"></i>
            Disclaimer & Information
          </h6>
        </div>
        <div class="card-body">
          <div class="disclaimer-content">
            <h6 class="mb-3 border-bottom pb-2">
              <i class="bi bi-file-earmark-ruled-fill me-2"></i>
              Address Verification Disclaimer
            </h6>
            <p class="text-xs mb-4">
              This Address Verification Report is sourced from credit bureau
              records and other authorized data providers. It reflects the
              information available at the moment of report generation and
              remains unaltered by The Good People Data Company.
            </p>

            <h6 class="mb-3 border-bottom pb-2">
              <i class="bi bi-shield-lock-fill me-2"></i>
              Data Privacy
            </h6>
            <p class="text-xs mb-4">
              All personal data contained herein is processed in accordance with
              applicable data protection legislation, including the Protection
              of Personal Information Act (POPIA). Access is restricted to duly
              authorized personnel for legitimate business purposes only.
            </p>

            <h6 class="mb-3 border-bottom pb-2">
              <i class="bi bi-tools me-2"></i>
              Technical Notes
            </h6>
            <p class="text-xs mb-4">
              Address information is obtained from credit bureau systems and may
              not capture the latest updates or could be subject to reporting
              delays. Report accuracy depends on the quality and completeness of
              the source data.
            </p>

            <div class="text-center text-muted mt-3">
              <p class="mb-0 text-xs">
                © {{ new Date().getFullYear() }} The Good People Data Company.
                All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </div>
      <!-- Report Footer -->
      <div class="report-footer">
        <span
          >Generated: {{ generated_date }} | Reference: {{ report_reference
          }}</span
        >
        <span
          >Page {{ 1 + paginated_address_history.length + 2 }} of {{ 1 +
          paginated_address_history.length + 2 }}</span
        >
      </div>
    </SectionSheet>
  </PageComponent>
</div>
